import type { ReportResponseDto } from '@/api/WhisApi/generated';
import { FoldableSection } from '@/components/FoldableSection/FoldableSection';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { getStep1FormConfig } from '@/screens/ReportCreation/forms/step1.config';
import type { FormSection } from '@/screens/ReportCreation/reportCreation.types';
import { ReportDetailSection } from './ReportDetailSection';
import type { DetailFieldLabelOverrides } from '../reportDetail.types';
import {
  getReportFieldValue,
  hasMissingRequiredFields,
} from '../reportDetail.utils';
import { ReportDetailFieldRenderer } from './ReportDetailFieldRenderer/ReportDetailFieldRenderer';

interface ReportDetailGeneralInformationProps {
  report: ReportResponseDto;
  isDraftStatus: boolean;
  defaultOpen?: boolean;
}

export const ReportDetailGeneralInformation = ({
  isDraftStatus,
  report,
  defaultOpen = false,
}: ReportDetailGeneralInformationProps): React.ReactElement => {
  const { t, language } = useAppTranslation();

  const sections: FormSection[] = getStep1FormConfig(t, language).sections ?? [];
  const generalInfoMissing = hasMissingRequiredFields(sections, (fieldId) =>
    getReportFieldValue(report, fieldId, language),
  );
  const labelOverrides: DetailFieldLabelOverrides = {
    species: t('reportDetail.fields.speciesAndOrder'),
    location: t('reportDetail.fields.locationCoordinates'),
    inaccurateLocation: t('reportDetail.fields.locationAccuracyFlag'),
    dateOfDetection: t('reportCreation.step1.fields.dateOfDetection'),
  };

  return (
    <ReportDetailSection>
      <FoldableSection
        title={t('reportDetail.sections.generalInformation')}
        defaultOpen={defaultOpen}
        showSectionIndicator={isDraftStatus && generalInfoMissing}
        sectionIndicatorLabel={t('reportDetail.missingIndicator')}
      >
        <ReportDetailFieldRenderer
          sections={sections}
          labelOverrides={labelOverrides}
          source={{ kind: 'report', report }}
        />
      </FoldableSection>
    </ReportDetailSection>
  );
};
